import PropTypes from 'prop-types';
import React, { forwardRef, useMemo } from 'react';
import AgGrid from 'components/molecules/agGrid';
import { ContactOfRenderer } from 'pages/contactsExtended/contactsRenderers/contactOfRenderer';
import { useHiresTableRows } from './hooks/useHiresTableRows';
import { HireContactRenderer } from './hiresRenderers/hireContactRenderer';
import { HireCompanyRenderer } from './hiresRenderers/hireCompanyRenderer';
import { HireJobTitleRenderer } from './hiresRenderers/hireJobTitleRenderer';
import { HireTeamInvolvementRenderer } from './hiresRenderers/hireTeamInvolvementRenderer';
import { HireDateRenderer } from './hiresRenderers/hireDateRenderer';
import { ContactSourceRender } from '../contactsExtended/contactsRenderers/contactSourceRender';

export const HiresTableView = forwardRef(
  (
    {
      data,
      activeNetwork,
      columns,
      setColumns,
      onLoadMore,
      hasMore,
      isLoadingMore,
      onSort,
      initialSortBy,
      sx,
      showSkeleton,
    },
    ref,
  ) => {
    const rows = useHiresTableRows(data, showSkeleton);

    const sortableColumns = useMemo(
      () => [
        {
          accessor: 'hiredDate',
          Header: 'Hired date',
          disableSortBy: false,
        },
        {
          accessor: 'teamInvolvement',
          Header: 'Assisted by team',
          disableSortBy: false,
        },
      ],
      [],
    );

    const defaultInitialSortBy = useMemo(
      () => ({
        id: 'hiredDate',
        desc: true,
      }),
      [],
    );

    return (
      <AgGrid
        ref={ref}
        disablePinnedOnMobile
        initialSortBy={initialSortBy || defaultInitialSortBy}
        sortColumns={sortableColumns}
        enableLeftTableBorder={false}
        useExternalStoredColumns
        storedColumns={columns}
        setStoredColumns={setColumns}
        itemName="hire"
        isInitialized
        canSort
        onSort={([item]) => {
          if (onSort) {
            onSort({
              sortBy: item.id,
              sortDirection: item.desc ? 'desc' : 'asc',
            });
          }
        }}
        canSelect={false}
        hasMore={hasMore}
        loadMore={onLoadMore}
        items={rows}
        totalItems={rows.length}
        columnDefinition={columns}
        isLoadingMore={isLoadingMore}
        onColumnAction={() => {}}
        persistColumnKey={`${activeNetwork?.id}-hires-columns-v3`}
        showActions={false}
        cellStyle={{
          padding: '0px',
        }}
        components={{
          hireContactRenderer: HireContactRenderer,
          hireCompanyRenderer: HireCompanyRenderer,
          hireJobTitleRenderer: HireJobTitleRenderer,
          hireTeamInvolvementRenderer: HireTeamInvolvementRenderer,
          hireDateRenderer: HireDateRenderer,
          contactSourceRenderer: ContactSourceRender,
          contactOfRenderer: ContactOfRenderer,
        }}
        rowActions={{
          height: 330,
          items: [],
        }}
        sx={{
          height: 'calc(100vh - 370px)',
          '.ag-root-wrapper': {
            border: 'none',
          },
          '.ag-header-cell[col-id="contact"]': {
            paddingLeft: '16px',
          },
          '@media screen and (max-width: 992px)': {
            '[col-id="selected"], .ag-pinned-left-header ': {
              borderRight: 'none !important',
            },
          },
          ...sx,
        }}
        gridOptions={{
          getRowClass: () => {},
          rowHeight: 48,
        }}
      />
    );
  },
);

HiresTableView.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  activeNetwork: PropTypes.object,
  columns: PropTypes.array,
  setColumns: PropTypes.func,
  onLoadMore: PropTypes.func,
  hasMore: PropTypes.bool,
  isLoadingMore: PropTypes.bool,
  onSort: PropTypes.func,
  initialSortBy: PropTypes.shape({
    id: PropTypes.string,
    desc: PropTypes.bool,
  }),
  sx: PropTypes.object,
  showSkeleton: PropTypes.bool,
};

HiresTableView.defaultProps = {
  activeNetwork: {},
  columns: null,
  setColumns: () => {},
  onLoadMore: () => {},
  hasMore: false,
  isLoadingMore: false,
  onSort: null,
  initialSortBy: null,
  sx: {},
  showSkeleton: false,
};
